import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  ValidationErrors,
} from '@angular/forms';
import { OtpChannel } from '../../../../services/strong-customer-authentication/model';
import { OtpInputComponent } from '../otp-input/otp-input.component';
import { AnalyticsService } from '../../../../services/analytics.service';
import { GTMTracking } from '../../../../enums/google-analytics-tracking';

@Component({
  selector: 'otp-form-control',
  template: `
    <div class="otp-form-control">
      <otp-input
        #otpInputComponent
        (blur)="onTouch()"
        ariaLabel="Please enter your 6 digit OTP"
        (onChange)="otpValue = $event"
        [submitted]="isSubmitted"
        requiredErrorMessage="OTP is required"
      >
      </otp-input>

      <p class="resendLimitReached" *ngIf="resendLimitReached">
        <b>You have reached your maximum resend limit.</b>
      </p>

      <p *ngIf="!resendLimitReached" class="resend-link">
        <toast *ngIf="isResent" [message]="message"></toast>
        <resend-countdown
          [resendSuccessful]="isResent"
          [text]="
            'If your 6 digit OTP has not arrived you can request it to be'
          "
          [resendLinkText]="'resent again'"
          (onResentRequested)="resendHandler()"
        >
        </resend-countdown>
      </p>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      // eslint-disable-next-line no-use-before-define
      useExisting: forwardRef(() => OtpFormControlComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      // eslint-disable-next-line no-use-before-define
      useExisting: OtpFormControlComponent,
    },
  ],
})
export class OtpFormControlComponent
  implements ControlValueAccessor, Validator
{
  @ViewChild('otpInputComponent') otpInputComponent: OtpInputComponent;
  OtpChannel: typeof OtpChannel = OtpChannel;
  readonly VALID_OTP_LENGTH = 6;
  // deprecated
  @Input()
  sentTo: string;
  @Input()
  otpChannel: OtpChannel;
  @Input()
  isResent: boolean;
  @Input()
  resendLimitReached: boolean;
  @Input()
  formName: string;
  @Output()
  resend = new EventEmitter();
  @Output()
  callAgain = new EventEmitter();
  onTouch: Function = Function.prototype;
  @Input()
  isDisabled: boolean;
  @Input()
  isSubmitted: boolean;

  private _otpValue: string;
  private onChange: Function = Function.prototype;
  private control: AbstractControl;

  constructor(private analyticsService: AnalyticsService) {}

  set otpValue(newValue: string) {
    this._otpValue = newValue;
    this.onChange(this._otpValue);
  }

  get otpValue(): string {
    return this._otpValue;
  }

  get message() {
    return this.otpChannel == OtpChannel.Text
      ? 'Your OTP has been resent'
      : "We've just called you and provided a new code";
  }

  resendHandler(): void {
    if (this.otpChannel === OtpChannel.Text) {
      this.onResend();
    } else {
      this.requestCallAgain();
    }
  }

  writeValue(value: string): void {
    this.otpValue = value;
  }

  registerOnChange(fn: (_: any) => {}): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onTouch = fn;
  }

  setDisabledState?(disabled: boolean): void {
    this.isDisabled = disabled;
  }

  validate(c: AbstractControl): ValidationErrors | null {
    if (!this.control) {
      this.control = c;
    }

    if (!this.control.value) {
      return {
        otp: true,
      };
    }

    const otpLength = this.control.value.length;

    if (otpLength !== this.VALID_OTP_LENGTH) {
      return {
        otp: {
          actualLength: otpLength,
          expectedLength: this.VALID_OTP_LENGTH,
        },
      };
    }

    return null;
  }

  hasError(): boolean {
    return (
      this.control &&
      this.control.invalid &&
      (this.control.touched || this.control.dirty)
    );
  }

  userChangedDefaultValues(): boolean {
    return this.control.dirty;
  }

  private onResend(): void {
    this.isResent = false;
    this.resend.emit();
    if (this.formName === 'register') {
      this.analyticsService.trackCtaActions(GTMTracking.ResendOtp);
    }
  }

  private requestCallAgain(): void {
    this.otpValue = undefined;
    this.callAgain.emit();
  }
}
